import React, { useEffect, useState } from 'react';
import { Heading } from 'src/components/Form';
import ChangeEmailDialogue from 'src/components/ChangeEmailDialogue/ChangeEmailDialogue';
import { useChangeEmailDialogue } from 'src/components/ChangeEmailDialogue/useChangeEmailDialogue';
import { ChangeEmailViewModel } from 'src/components/ChangeEmailDialogue/changeEmailViewModel';
import 'src/components/ChangeEmailDialogue/change-email-dialogue.scss';
import Button from 'src/components/Button';
import Callout from 'src/components/InformationCallout/Callout';
import Col from 'src/components/Col';
import Input from 'src/components/Input';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import ProfileImageField from 'src/components/ProfileImageField/ProfileImageField';
import Card from 'src/components/Card/Card';
import './AccountSettingsSection.scss';
import Row from 'src/components/Row';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import { onCheckDocument } from 'src/helpers/CommonHelpers';
import { USER_ROLE_NAMES } from 'src/pages/Settings/types';
import {
  useAccountSettings,
  UseAccountSettingsReturn,
} from 'src/pages/Settings/CustomHooks/useAccountSettings';
import {
  FetchContactByIdResult,
  useContactAPI,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import { CalendlyAPIKeyVisibilityIcon } from 'src/pages/Settings/components/CalendlyAPIKeyVisibilityIcon/CalendlyAPIKeyVisibilityIcon';
import FontAwesomeIcon from 'src/components/Icon';
import NewBadge from 'src/components/NewBadge/NewBadge';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import calcomLogo from 'src/pages/Calendar/CalcomIntegration/calcom-logo.svg';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import calcomCreateAccountPreview from 'src/pages/Calendar/CalcomIntegration/assets/calcom-create-account-preview.png';
import '../../Settings.scss';

export const AccountSettingsSection: React.FC = () => {
  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false);
  const [allowEmailChange, setAllowEmailChange] = useState<boolean>(true);
  const [isUnlinkCalendlyOpen, setIsUnlinkCalendlyOpen] =
    useState<boolean>(false);

  const {
    settings,
    errors,
    calendlyAPIKeyHidden,
    showCalendarFields,
    user,
    isLoading,
    isDirty,
    stagedPreviewUrl,
    hasStagedImage,
    updateField,
    handleSubmit,
    unlinkCalendly,
    clearSaveState,
    toggleCalendlyKeyVisibility,
  }: UseAccountSettingsReturn = useAccountSettings();

  const changeEmailViewModel: ChangeEmailViewModel = useChangeEmailDialogue(
    settings?.email ?? ''
  );

  const handleConfirmUnlinkCalendly = async (): Promise<void> => {
    const success: boolean = await unlinkCalendly();

    if (success) {
      setIsUnlinkCalendlyOpen(false);
    }
  };

  const { fetchContactById } = useContactAPI();

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const loadAllowEmailChange = async (): Promise<void> => {
      const result: FetchContactByIdResult = await fetchContactById(user.id);

      if (result.ok && result.contact) {
        const flag: boolean | undefined = result.contact.allowEmailChange;
        const resolved: boolean = typeof flag === 'boolean' ? flag : true;

        setAllowEmailChange(resolved);
      }
    };

    void loadAllowEmailChange();
  }, [user?.id]);

  const canChangeEmail: boolean = allowEmailChange;

  // Calendly is being retired in favour of Cal.com. Accounts that already have a Calendly key/link
  // keep the editable fields (so existing Service Request scheduling isn't broken); accounts with
  // nothing linked see the Cal.com migration callout instead of fresh Calendly fields.
  const isCalendlyLinked: boolean = Boolean(
    settings?.calendlyAPIKey?.trim() || settings?.calendarLink?.trim()
  );

  const userRoleName: string | undefined = user?.roles?.[0]?.role?.name;

  const firstName: string = user?.firstname ?? settings?.firstname ?? '';
  const lastName: string = user?.lastname ?? settings?.lastname ?? '';
  const profileName: string | undefined =
    `${firstName} ${lastName}`.trim() || undefined;

  const canManageContacts: boolean =
    userRoleName === USER_ROLE_NAMES.PROPERTY_DEVELOPER ||
    userRoleName === USER_ROLE_NAMES.ADMIN ||
    userRoleName === USER_ROLE_NAMES.SUPER_ADMIN;

  const calloutSubtitle: React.ReactNode = (
    <>
      Email changes are disabled for this user. Contact your property developer
      or administrator if this isn&apos;t right.
      {canManageContacts && (
        <>
          {' '}
          You can manage this from the{' '}
          <Link
            to={PATHS.CONTACTS_LIST}
            style={{
              fontWeight: 700,
              textDecoration: 'underline',
              color: 'inherit',
            }}
          >
            contacts page
          </Link>
          .
        </>
      )}
    </>
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="settingsSection">
        <Heading>Account Settings</Heading>
        <div className="settings-profile-card">
          <Card
            bordered
            body={
              <ProfileImageField
                imageUrl={settings?.profileImage}
                stagedPreviewUrl={stagedPreviewUrl}
                isStaged={hasStagedImage}
                name={profileName}
                subtitle={userRoleName}
                avatarSize="settings-profile"
                loading={!settings}
                onSelectImage={(files: File[]) =>
                  onCheckDocument(files) &&
                  updateField('selectedProfileImage', files)
                }
                onClearStagedImage={() =>
                  updateField('selectedProfileImage', [])
                }
                saveActionLabel={"Save Account Settings"}
              />
            }
          />
        </div>
        <Row>
          <Col xs={12} md={6}>
            <Input
              label="First Name"
              placeholder="First Name"
              value={settings?.firstname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('firstname', e.target.value)
              }
              onfocus={clearSaveState}
              block
              errors={errors.firstname}
            />
          </Col>
          <Col xs={12} md={6}>
            <Input
              label="Last Name"
              placeholder="Last Name"
              value={settings?.lastname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('lastname', e.target.value)
              }
              onfocus={clearSaveState}
              block
              errors={errors.lastname}
            />
          </Col>
        </Row>
        <div className="email-field-row">
          <Input
            label="Email"
            placeholder="Email"
            value={settings?.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateField('email', e.target.value)
            }
            onfocus={clearSaveState}
            block
            disabled
            errors={errors.email}
          />
          {canChangeEmail && (
            <>
              <button
                type="button"
                className="email-edit-button"
                title="Change email"
                onClick={() => setIsChangeEmailOpen(true)}
              >
                <FontAwesomeIcon glyph="pencil" weight="light" />
                <div style={{ marginBottom: '0.35rem' }}>
                  <NewBadge spaced />
                </div>
              </button>
            </>
          )}
        </div>
        {!canChangeEmail && (
          <Callout
            variant="info"
            title="Email changes disabled"
            subtitle={calloutSubtitle}
          />
        )}
        <ChangeEmailDialogue
          isOpen={isChangeEmailOpen}
          onClose={() => setIsChangeEmailOpen(false)}
          viewModel={changeEmailViewModel}
          title="Change email"
          description="A verification link will be sent to your new email address."
          maxWidth="70rem"
          minHeight="50rem"
        />
        {isUnlinkCalendlyOpen && (
          <ConfirmationModal
            className="calendly-unlink-modal"
            variant="danger"
            title="Unlink Calendly?"
            message="This disconnects your Calendly account from Juniper."
            warningMessage={
              <>
                Cal.com is still in beta and{' '}
                <strong>doesn&apos;t support Service Requests yet</strong> —
                Calendly is the only external third party thing scheduling them right now. Unlinking{' '}
                <strong>disables Service Request scheduling entirely</strong>{' '}
                until Cal.com supports it, and can{' '}
                <strong>disrupt active Calendly-scheduled events</strong>, so
                wrap up any ongoing bookings first. Reconnecting on your own
                isn&apos;t possible — you&apos;ll need to contact support to
                reverse this.
              </>
            }
            confirmLabel="Unlink Calendly"
            cancelLabel="Cancel"
            requireTypeToConfirm
            confirmationWord="I UNDERSTAND AND UNLINK"
            isSubmitting={isLoading}
            onConfirm={handleConfirmUnlinkCalendly}
            onCancel={() => setIsUnlinkCalendlyOpen(false)}
          />
        )}
        <Input
          label="Phone"
          placeholder="Phone"
          value={settings?.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateField('phone', e.target.value)
          }
          onfocus={clearSaveState}
          block
          errors={errors.phone}
        />
        {showCalendarFields &&
          (isCalendlyLinked ? (
            <div className="calendly-deprecated">
              <div className="calendly-deprecated-header">
                <span className="calendly-deprecated-flag">
                  <FontAwesomeIcon
                    glyph="exclamation-triangle"
                    weight="solid"
                  />
                </span>
              </div>

              <Callout
                variant="warning"
                title="Calendly is being phased out"
                subtitle="Scheduling is moving to Cal.com, which is still in beta — it currently powers key-pickup and walkthrough scheduling only. Service Requests have no Cal.com equivalent yet, so your existing Calendly connection is what keeps Service Request scheduling working. Keep it linked until Cal.com supports Service Requests."
              />

              <Input
                label="Calendaring link"
                placeholder="Calendar link"
                value={settings?.calendarLink ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('calendarLink', e.target.value)
                }
                disabled
                block
              />
              <Row>
                <Col xs={12} md={11}>
                  <Input
                    label="Calendly API Key"
                    placeholder="Calendly API Key"
                    value={settings?.calendlyAPIKey ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateField('calendlyAPIKey', e.target.value)
                    }
                    onfocus={clearSaveState}
                    disabled
                    block
                    mask={calendlyAPIKeyHidden}
                  />
                </Col>
                <Col>
                  <span className="calendly-key-visibility">
                    <CalendlyAPIKeyVisibilityIcon
                      isHidden={calendlyAPIKeyHidden}
                      onToggle={toggleCalendlyKeyVisibility}
                    />
                  </span>
                </Col>
              </Row>

              <div className="calendly-deprecated-actions">
                <Button
                  type="button"
                  label="Unlink Calendly"
                  size="small"
                  outline="red"
                  onClick={() => setIsUnlinkCalendlyOpen(true)}
                />
              </div>
            </div>
          ) : (
            <div className="calcom-migration">
              <Callout
                variant="info"
                title="Looking for Calendly? External scheduling now runs on Cal.com"
                subtitle="We've moved self-scheduling from Calendly to Cal.com — there's nothing to set up here anymore. Connect Cal.com from the calendar instead."
              >
                <ul className="calcom-migration-benefits">
                  <li>
                    Real-time availability from your connected calendars
                    (Google, Outlook, and more) — no more copying API keys.
                  </li>
                  <li>
                    Powers homeowner self-booking for key pickups and
                    walkthroughs.
                  </li>
                  <li>
                    Reschedules and cancellations sync back to Juniper
                    automatically.
                  </li>
                </ul>
                <div className="calcom-migration-preview">
                  <LinkPreviewCard
                    href="https://cal.com/help/quick-start/create-account"
                    title="Create your Cal.com account"
                    description="Sign up and link your Google or Outlook calendar — a quick one-time setup."
                    siteName="Cal.com Help"
                    image={calcomCreateAccountPreview}
                  />
                </div>
                <Link
                  to={PATHS.CALENDAR_CALCOM}
                  className="calcom-migration-cta"
                >
                  <Button
                    color="teal"
                    size="small"
                    label={
                      <span className="calcom-migration-button-label">
                        Set up
                        <span className="calcom-migration-button-pill">
                          <img src={calcomLogo} alt="Cal.com" />
                        </span>
                      </span>
                    }
                  />
                </Link>
              </Callout>
            </div>
          ))}
        <div className="account-save-actions">
          <LoadingButton
            actionText="Save Account Settings"
            isSubmitting={isLoading}
            disabled={!isDirty}
            submit
          />
        </div>
      </div>
    </form>
  );
};
